import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScrollText, Loader2, ShieldCheck } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

// Route prefixes we DO gate the app on. Public marketing/legal routes are
// intentionally exempt so users can still read the docs before re-accepting.
const GATED_PREFIXES = [
    "/professional",
    "/employer",
    "/recruiter",
    "/team",
    "/admin",
    "/inbox",
    "/notifications",
    "/settings",
    "/dashboard",
];

const CHECKBOX_TEXT = {
    terms: "I agree to the updated Terms of Service.",
    privacy: "I have read and acknowledge the updated Privacy Policy.",
    verification_consent: "I acknowledge the updated Verification & AI Consent Policy.",
};

const CHECKBOX_KEY = {
    terms: "terms",
    privacy: "privacy",
    verification_consent: "verify.identity", // treated as verification_consent doc
};

function pathIsGated(pathname) {
    return GATED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

/**
 * AcceptanceGate — mounts once at the app root. On login (or route change),
 * fetches the user's acceptance status. If any of the three legal docs has
 * doc_status.accepted=false AND the current route is behind auth, we render a
 * blocking modal that re-collects consent. Public routes (marketing, /login,
 * /signup, /terms, /privacy, /verification-consent-policy) are exempt.
 */
export default function AcceptanceGate() {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [checked, setChecked] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        if (!user) { setStatus(null); return; }
        try {
            const { data } = await api.get("/legal/my-acceptances");
            setStatus(data);
        } catch {
            setStatus(null);
        }
    }, [user]);

    useEffect(() => { load(); }, [load]);

    const outdated = React.useMemo(() => {
        if (!status?.doc_status) return [];
        return Object.entries(status.doc_status)
            .filter(([, v]) => !v.accepted)
            .map(([doc_id, v]) => ({ doc_id, ...v }));
    }, [status]);

    const shouldGate = user
        && outdated.length > 0
        && pathIsGated(typeof window !== "undefined" ? window.location.pathname : "");

    const canSubmit = outdated.every((d) => !!checked[d.doc_id]);

    const submit = async () => {
        if (!canSubmit) return;
        try {
            setSubmitting(true);
            const acceptances = {};
            for (const d of outdated) {
                const key = CHECKBOX_KEY[d.doc_id] || d.doc_id;
                acceptances[key] = true;
            }
            await api.post("/legal/accept", { acceptances });
            toast.success("Thanks — your updated agreements are recorded.");
            setChecked({});
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (!shouldGate) return null;

    return (
        <Dialog open modal>
            <DialogContent
                className="max-w-lg"
                data-testid="acceptance-gate"
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-heading font-medium text-xl">
                        <ScrollText size={18} className="text-emerald-500" /> Please review updated agreements
                    </DialogTitle>
                    <DialogDescription>
                        We&apos;ve updated the following legal documents since you last accepted. Please review and re-confirm before continuing.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2 max-h-[52vh] overflow-y-auto pr-1">
                    {outdated.map(({ doc_id, doc, last_accepted_version }) => (
                        <div key={doc_id} className="rounded-xl border border-border p-4" data-testid={`acceptance-item-${doc_id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="font-heading font-semibold text-sm text-foreground truncate">
                                        {doc.title}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                                        Now at version {doc.version}
                                        {last_accepted_version && (
                                            <> · you last accepted {last_accepted_version}</>
                                        )}
                                    </div>
                                </div>
                                <Link
                                    to={doc.route || "#"}
                                    target="_blank"
                                    className="text-[11px] font-semibold text-emerald-600 hover:underline shrink-0"
                                    data-testid={`acceptance-open-${doc_id}`}
                                >
                                    Read →
                                </Link>
                            </div>
                            {doc.changelog && (
                                <div className="mt-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 p-2 text-xs text-foreground">
                                    <span className="font-semibold text-emerald-700 dark:text-emerald-400">What changed:</span> {doc.changelog}
                                </div>
                            )}
                            <label className="mt-3 flex items-start gap-2 text-xs text-foreground cursor-pointer">
                                <Checkbox
                                    checked={!!checked[doc_id]}
                                    onCheckedChange={(v) => setChecked((c) => ({ ...c, [doc_id]: !!v }))}
                                    className="mt-0.5"
                                    data-testid={`acceptance-check-${doc_id}`}
                                />
                                <span>{CHECKBOX_TEXT[doc_id] || `I acknowledge the updated ${doc.title}.`}</span>
                            </label>
                        </div>
                    ))}
                </div>

                <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                    <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                        <ShieldCheck size={11} className="text-emerald-500" /> Your acceptance is logged with timestamp + IP.
                    </span>
                    <button
                        onClick={submit}
                        disabled={!canSubmit || submitting}
                        className="btn-green text-sm px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60"
                        data-testid="acceptance-submit"
                    >
                        {submitting ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} Confirm &amp; continue
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
